/**
 * SegmentedToggle — generic two-or-more option segmented control used by
 * domain-specific toggles across workspace tabs (``Per fonte``/``Per
 * data`` in documents, ``Per thread``/``Per messaggio`` in inbox, etc.).
 *
 * Pattern parallels :class:`SearchPowerSelector` (composer): pressed
 * button gets a white background + shadow; aria-pressed mirrors the
 * active state. Pure renderer — caller owns the value + URL plumbing.
 *
 *   ┌─ fieldset bg-ink-1/[0.04] ─────────────────────┐
 *   │ [⊟ Active]   [⊞ Idle]                          │
 *   └─────────────────────────────────────────────────┘
 *
 * Use it as the unifier for tab-level groupings/filters that the user
 * toggles ON the page, NOT for navigation (use ``SubTabBar`` for that).
 */

import type * as React from "react";

import { cn } from "../lib/cn";


export interface SegmentedToggleOption<T extends string> {
  /** Discriminator returned via ``onChange``. */
  readonly id: T;
  /** Italian label rendered next to the icon. */
  readonly label: string;
  /** Optional leading glyph — typically a 12px Lucide icon. */
  readonly icon?: React.ReactNode;
}


export interface SegmentedToggleProps<T extends string> {
  /** Currently-active option id (must match one of the ``options.id``). */
  readonly value: T;
  /** Fired with the new option id when the user clicks a button. */
  readonly onChange: (next: T) => void;
  /** Display order — caller decides; leftmost is the natural default. */
  readonly options: ReadonlyArray<SegmentedToggleOption<T>>;
  /** Required ``aria-label`` for the ``<fieldset>`` so AT users hear the
   *  axis name (``Modalità di raggruppamento`` etc.). */
  readonly ariaLabel: string;
  readonly className?: string;
}


export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: SegmentedToggleProps<T>): React.ReactElement {
  return (
    <fieldset
      aria-label={ariaLabel}
      className={cn(
        // Recessed warm paper track + inset hairline, concentric with the
        // raised thumb — same segmented look as SegmentedControl.
        "inline-flex items-center gap-0.5 rounded-[12px] border-0 p-[3px]",
        "bg-[rgba(60,40,20,0.04)] shadow-[inset_0_0_0_1px_rgba(60,40,20,0.06)]",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.id)}
            className={cn(
              "relative inline-flex h-7 items-center gap-1.5 rounded-[9px] px-3 text-[12px] leading-none transition-all duration-150 ease-smooth",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              active
                ? "bg-white font-semibold text-ink-1 shadow-[0_0_0_1px_rgba(60,40,20,0.07),0_1px_2px_rgba(60,40,20,0.06)]"
                : "font-medium text-ink-2 hover:text-ink-1",
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </fieldset>
  );
}
SegmentedToggle.displayName = "SegmentedToggle";
